import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';

export const useEmployeeLocations = () => {
  const { company } = useAuth();
  const [employeeLocations, setEmployeeLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEmployeeLocations = useCallback(async () => {
    if (!company?.id) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/api/companies/${company.id}/latest-locations?excludeNoLocation=false`);
      
      if (response.data?.data?.locations) {
        setEmployeeLocations(response.data.data.locations);
      } else {
        setEmployeeLocations([]);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching employee locations:', err);
      setError('Failed to load employee locations');
      setEmployeeLocations([]);
    } finally {
      setLoading(false);
    }
  }, [company?.id]);

  const getEmployeeLocation = useCallback((employeeId) => {
    return employeeLocations.find(loc => 
      loc.employee && loc.employee.id === employeeId
    );
  }, [employeeLocations]);

  const getEmployeeDistance = useCallback((employeeId, targetLocation) => {
    const empLoc = getEmployeeLocation(employeeId);
    
    if (!empLoc || !empLoc.location || !empLoc.location.coords) {
      return null;
    }

    if (!targetLocation?.coordinates?.lat || !targetLocation?.coordinates?.lng) {
      return null;
    }

    // Check if location is stale (older than 24 hours)
    const lastUpdated = new Date(empLoc.lastUpdated);
    const now = new Date();
    const ageMs = now - lastUpdated;
    
    if (ageMs > 24 * 60 * 60 * 1000) {
      return null;
    }

    // Calculate distance using Haversine formula
    const toRad = (value) => value * Math.PI / 180;
    const R = 6371; // Earth's radius in kilometers
    
    const lat1 = targetLocation.coordinates.lat;
    const lon1 = targetLocation.coordinates.lng;
    const lat2 = empLoc.location.coords.latitude;
    const lon2 = empLoc.location.coords.longitude;
    
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) + 
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * 
              Math.sin(dLon/2) * Math.sin(dLon/2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    return {
      distance: distance,
      formatted: `${distance.toFixed(2)} km away`,
      isStale: false
    };
  }, [getEmployeeLocation]);

  const getEmployeesWithLocations = useCallback(() => {
    return employeeLocations.filter(loc => 
      loc.location && loc.location.coords
    );
  }, [employeeLocations]);

  const refreshLocations = useCallback(() => {
    fetchEmployeeLocations();
  }, [fetchEmployeeLocations]);

  // Auto-fetch when component mounts or company changes
  useEffect(() => {
    if (company?.id) {
      fetchEmployeeLocations();
    }
  }, [company?.id, fetchEmployeeLocations]);

  return {
    employeeLocations,
    loading,
    error,
    fetchEmployeeLocations,
    getEmployeeLocation,
    getEmployeeDistance,
    getEmployeesWithLocations,
    refreshLocations
  };
}; 